import React, { useEffect, useRef, useState } from 'react';
import { useLocation, NavLink } from 'react-router-dom';
import { Header } from '../layout/Header';
import { cn } from '../../utils/cn';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const adminNavigation = [
  { name: 'Dashboard', href: '/admin', icon: '📊' },
  { name: 'Organizations', href: '/admin/organizations', icon: '🏢' },
  { name: 'Locations', href: '/admin/locations', icon: '📍' },
  { name: 'Users', href: '/admin/users', icon: '👥' },
  { name: 'Clients', href: '/admin/clients', icon: '👤' },
  { name: 'Treatments', href: '/admin/treatments', icon: '🏥' },
  { name: 'Notes', href: '/admin/notes', icon: '📝' },
  { name: 'Tickets', href: '/admin/tickets', icon: '🎫' },
  { name: 'Workflows', href: '/admin/workflows', icon: '⚙️' },
];

export function AdminLayout({ children }: AdminLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const mainRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTo({ top: 0, behavior: 'auto' });
    }
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [location.pathname, location.search]);

  return (
    <div className="h-screen flex overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 bg-white/95 backdrop-blur-lg shadow-2xl transform transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 border-r border-gray-200/50',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
          sidebarExpanded ? 'w-64' : 'w-20'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo/Header */}
          <div className="flex items-center justify-between h-20 border-b border-gray-200/50 px-4">
            {sidebarExpanded && (
              <h2 className="text-xl font-bold text-primary-600">Admin Panel</h2>
            )}
            <button
              onClick={() => setSidebarExpanded(!sidebarExpanded)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Toggle sidebar"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-8 space-y-3 overflow-y-auto">
            {adminNavigation.map((item, index) => (
              <NavLink
                key={item.name}
                to={item.href}
                title={item.name}
                className={({ isActive }) =>
                  cn(
                    'group flex items-center px-3 py-3 text-sm font-medium rounded-xl transition-all duration-200',
                    sidebarExpanded ? 'justify-start' : 'justify-center',
                    isActive
                      ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg shadow-primary-500/25'
                      : 'text-gray-600 hover:bg-gradient-to-r hover:from-primary-50 hover:to-secondary-50 hover:text-gray-900 hover:shadow-md'
                  )
                }
                onClick={() => setSidebarOpen(false)}
              >
                <span className={cn(
                  "text-2xl transition-transform duration-200",
                  "group-hover:scale-110",
                  sidebarExpanded ? "mr-3" : ""
                )}>{item.icon}</span>
                {sidebarExpanded && (
                  <span className="whitespace-nowrap overflow-hidden transition-all duration-300">
                    {item.name}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>

      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex flex-col flex-1 overflow-hidden">
        <Header onToggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
        
        <main
          ref={mainRef}
          className="flex-1 relative overflow-y-auto focus:outline-none pt-20 md:pt-0 flex flex-col"
        >
          <div className="py-8 flex-1">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}





